import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Skeleton, styled, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import { clearShipperDetailsData } from '../../../state/features/shipperDetailsSlice';
import { publicRoutes } from '../../../routes/appRouteList';
import { ApiStatus } from '../../../utils/enums';
import { truncateId } from '../../../utils/formattingUtils';
import DetailHeader from './DetailHeader';

const classesPrefix = 'shipperDetailsHeader';

const classes = {
    loading: `${classesPrefix}-loading`,
};

const StyledSkeleton = styled(Skeleton)(({ theme }) => {
    return {
        [`&.${classes.loading}`]: {
            backgroundColor: theme.palette.grey[100],
        },
    };
});

const ShipperDetailsHeader = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ id: string; }>();
    const theme = useTheme();
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

    const shipperDetailsApiStatus = useAppSelector((state) => { return state.shipper.shipperDetailsApiStatus; });
    const shipperDetails = useAppSelector((state) => { return state.shipper.shipperDetails; });

    useEffect(() => {
        return () => {
            dispatch(clearShipperDetailsData());
        };
    }, [dispatch]);

    const renderShipperName = (): React.ReactNode => {
        if (shipperDetailsApiStatus === ApiStatus.Idle || shipperDetailsApiStatus === ApiStatus.Loading) {
            return <StyledSkeleton className={classes.loading} width={300} />;
        }

        if (isMdDown) {
            return truncateId(shipperDetails.pickup_party_name);
        }

        return shipperDetails.pickup_party_name;
    };

    if (shipperDetailsApiStatus === ApiStatus.Failure) {
        return (
            <Navigate
                to={`${publicRoutes.notFound}?requestedroute=${encodeURIComponent(`${location.pathname}${location.search}`)}&reason=${encodeURIComponent(`We tried, but we couldn't find a shipper named ${params.id}.`)}`}
            />
        );
    }

    return (
        <DetailHeader
            onBackClicked={(): void => {
                navigate(-1);
            }}
        >
            <Typography variant='h6'>
                {renderShipperName()}
            </Typography>
        </DetailHeader>
    );
};

export default ShipperDetailsHeader;
